import { ApolloClient, ApolloLink, InMemoryCache, createHttpLink, split } from '@apollo/client';
export { gql } from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { setContext } from '@apollo/client/link/context';
import { RetryLink } from '@apollo/client/link/retry';
import { createClient } from 'graphql-ws';
import { NEXT_PUBLIC_GRAPHQL_API, NEXT_PUBLIC_GRAPHQL_WS_API } from '@env';
import { getToken } from '@modules/auth-helpers';
// @ts-expect-error We are missing the type definitions for this module
import apolloLogger from 'apollo-link-logger';

// import { BatchHttpLink } from '@apollo/client/link/batch-http';

// const httpLink = new BatchHttpLink({
//   uri: NEXT_PUBLIC_GRAPHQL_API,
// });

const httpLink = createHttpLink({
  uri: NEXT_PUBLIC_GRAPHQL_API,
});

const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: Infinity,
    jitter: true,
  },
  attempts: {
    max: 5,
    retryIf: (error) => {
      if (error.message === 'INVALID_TOKEN') {
        return true;
      }

      return false;
    },
  },
});

const authLink = setContext(async (operation, { headers, isUnauthenticatedRequest }) => {
  if (typeof window === 'undefined') {
    return {
      headers,
    };
  }

  const token = await getToken();

  // Get an authorization header if it was set in the headers object. This would happen at the time of calling the query/mutation.
  // i.e. useMyQuery({ context: { headers: { authorization: "Bearer <token>" } } });
  const authorizationHeaderOverride = getAuthorizationHeaderOverride(headers);

  if (!token && !isUnauthenticatedRequest && !authorizationHeaderOverride) {
    throw new Error('INVALID_TOKEN');
  }

  const authorizationHeader = token ? `Bearer ${token}` : '';
  return {
    headers: {
      ...headers,
      authorization: authorizationHeaderOverride ? authorizationHeaderOverride : authorizationHeader,
      'X-APOLLO-OPERATION-NAME': operation.operationName,
    },
  };
});

/** Fetches an authorization header value from the headers object if it exists */
function getAuthorizationHeaderOverride(headers: Record<string, unknown>): string | undefined {
  const authHeader = headers?.authorization;

  if (typeof authHeader === 'string') {
    return authHeader;
  }

  return '';
}

const websocketLink =
  typeof window !== 'undefined'
    ? new GraphQLWsLink(
        createClient({
          url: NEXT_PUBLIC_GRAPHQL_WS_API,
          connectionParams: async () => {
            const token = await getToken();
            return {
              Authorization: token ? `Bearer ${token}` : '',
            };
          },
        })
      )
    : null;

const splitLink =
  typeof window !== 'undefined' && websocketLink !== null
    ? split(
        ({ query }) => {
          const def = getMainDefinition(query);
          return def.kind === 'OperationDefinition' && def.operation === 'subscription';
        },
        websocketLink,
        httpLink
      )
    : httpLink;

export const apolloClient = new ApolloClient({
  link: ApolloLink.from([
    ...(process.env.NODE_ENV === 'development' ? [apolloLogger] : []),
    retryLink,
    authLink,
    splitLink,
  ]),
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          getProfilesWithNoCards: {
            merge(_, incoming: []) {
              return [...incoming];
            },
          },
        },
      },
    },
  }),
});
